/**
 * Shopify Auto Cart - Versão Otimizada e Silenciosa (Evita Conflitos)
 * VERSÃO 3.3 - OVERLAY COM POSITION ABSOLUTE
 *
 * FUNCIONALIDADES:
 * 1. Adiciona/remove brindes com base em valores mínimos.
 * 2. ADIÇÃO SILENCIOSA: Adiciona o brinde sem disparar a atualização automática do carrinho.
 * 3. LIMPEZA AUTOMÁTICA: Se o valor total do carrinho for zero, mas houver itens, ele limpa o carrinho.
 * 4. SUPORTE A MÚLTIPLOS BRINDES (CUMULATIVOS): Todos os brindes elegíveis são mantidos no carrinho.
 * 5. IMPEDE que brindes sejam os únicos itens no carrinho.
 * 6. OCULTA o seletor de quantidade para produtos brinde.
 * 7. FEEDBACK VISUAL (notificação) quando brindes são adicionados.
 * 8. COMPATÍVEL COM PRESTIGE V7+ (web components) E VERSÕES ANTERIORES
 * 9. INTERCEPTA COMPRA DIRETA: Força atualização de brindes mesmo em "comprar agora" (direct buy)
 * 10. BLOQUEIO DE BOTÃO CORRIGIDO: Desabilita botão APENAS durante processamento de brindes
 * 11. OVERLAY POSITION ABSOLUTE: Mantém o botão totalmente intacto, sobrepõe elemento temporário
 * 
 * MUDANÇAS NA VERSÃO 3.3:
 * - Cria overlay com position: absolute sobre o botão
 * - Botão pai recebe position: relative
 * - Conteúdo original do botão permanece totalmente inalterado
 * - Overlay é removido completamente após desbloqueio
 */

(function() {
  'use strict';

  // ========== CONFIGURAÇÃO ==========
  const CONFIG = {
    GIFTS: [     
      {
        THRESHOLD: 40.00,
        PRODUCT_VARIANT_ID: 42983460831322, 
        NAME: "🎁 DUOCREAM ICE CREAM PINEAPPLE DELICIOUS GOURMAND 160G (Brinde)"
      },
    ],
    NOTIFICATION: {
      ENABLED: true,
      DURATION: 2500,
      POSITION: 'top-right'
    },
    OPTIMIZATION: {
      DEBOUNCE_DELAY: 1000,
      COOLDOWN_AFTER_SUCCESS: 2000,
    },
    DEBUG: false,
    // Seletor do botão de checkout
    CHECKOUT_BUTTON_SELECTOR: 'form.cart-drawer__footer button.button.w-full'
  };

  // ========== DETECÇÃO DE VERSÃO ==========
  let isPrestigeV7Plus = false;
  let fetchCartFunction = null;

  // Tentar importar fetchCart do tema (Prestige v7+)
  async function detectPrestigeVersion() {
    try {
      // Tenta importar a função do tema
      const themeModule = await import(window.Shopify?.theme?.theme?.asset_url?.replace('[filename]', 'theme.js') || '');
      if (themeModule && themeModule.fetchCart) {
        fetchCartFunction = themeModule.fetchCart;
        isPrestigeV7Plus = true;
        log('✓ Prestige v7+ detectado. Usando fetchCart do tema.');
        return true;
      }
    } catch (e) {
      log('ℹ Prestige v7+ não detectado. Usando fetch direto.');
    }
    
    // Fallback para versões anteriores
    fetchCartFunction = async () => {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      return response.json();
    };
    return false;
  }

  // ========== VARIÁVEIS GLOBAIS ==========
  let giftsInCart = new Map();
  let giftsBeingManaged = new Set();
  let isCheckingCart = false;
  let cooldownUntil = 0;
  let lastCartState = null;
  let debounceTimeout = null;
  let isProcessingDirectBuy = false;
  let isProcessingGifts = false;
  let currentOverlay = null;  // Referência ao overlay atual

  // ========== FUNÇÕES UTILITÁRIAS ==========
  function log(...args) {
    if (CONFIG.DEBUG) {
      console.log('[AutoCart]', ...args);
    }
  }

  function isInCooldown() {
    return Date.now() < cooldownUntil;
  }

  function startCooldown() {
    cooldownUntil = Date.now() + CONFIG.OPTIMIZATION.COOLDOWN_AFTER_SUCCESS;
    log('Cooldown iniciado por', CONFIG.OPTIMIZATION.COOLDOWN_AFTER_SUCCESS, 'ms');
  }
  
  function hasCartStateChanged(newCart) {
    if (!lastCartState) return true;
    const oldRelevant = {
      itemCount: lastCartState.item_count,
      totalPrice: lastCartState.total_price,
      variantIds: lastCartState.items.map(item => item.variant_id).sort()
    };
    const newRelevant = {
      itemCount: newCart.item_count,
      totalPrice: newCart.total_price,
      variantIds: newCart.items.map(item => item.variant_id).sort()
    };
    return JSON.stringify(oldRelevant) !== JSON.stringify(newRelevant);
  }

  // ========== SISTEMA DE BLOQUEIO COM OVERLAY ==========
  function injectOverlayStyles() {
    // Verificar se já foi injetado
    if (document.getElementById('auto-cart-overlay-styles')) return;

    const styleElement = document.createElement('style');
    styleElement.id = 'auto-cart-overlay-styles';
    styleElement.textContent = `
      /* Botão pai com position relative para contexto de posicionamento */
      form.cart-drawer__footer button.button.w-full {
        position: relative;
      }

      /* Overlay que sobrepõe o botão */
      .auto-cart-overlay {
        position: absolute;
        top: 0;
        left: 50%;
        transform: translate(-50%, 0)!important;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #000;
        color: #ffffff;
        cursor: not-allowed;
        border-radius: 6px;
        z-index: 1000;
        width: 90%;
        height: 100%;
        pointer-events: none;
        animation: auto-cart-pulse 1.5s infinite;
        padding: 5px;
      }

      /* Animação de pulsação */
      @keyframes auto-cart-pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: 0.8;
        }
      }

      /* Container de feedback dentro do overlay */
      .auto-cart-overlay-content {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        font-weight: 500;
        white-space: nowrap;
        text-align: center;
      }

      /* Spinner dentro do overlay */
      .auto-cart-overlay-spinner {
        display: inline-block;
        width: 12px;
        height: 12px;
        border: 2px solid rgba(255, 255, 255, 0.3);
        border-top-color: white;
        border-radius: 50%;
        animation: auto-cart-spin 0.8s linear infinite;
        flex-shrink: 0;
      }

      @keyframes auto-cart-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleElement);
    log('✓ Estilos de overlay injetados');
  }

  function lockCheckoutButtons(message = "Aguarde, o carrinho está sendo atualizado...") {
    log('🔒 Bloqueando botão de checkout com overlay...');
    isProcessingGifts = true;

    // Encontrar o botão específico
    const checkoutButton = document.querySelector(CONFIG.CHECKOUT_BUTTON_SELECTOR);
    
    if (!checkoutButton) {
      log('⚠️ Botão de checkout não encontrado com seletor:', CONFIG.CHECKOUT_BUTTON_SELECTOR);
      return;
    }

    // Se já existe um overlay, não criar outro
    if (currentOverlay) {
      log('ℹ Overlay já existe, ignorando novo bloqueio');
      return;
    }

    // Criar overlay
    const overlay = document.createElement('div');
    overlay.className = 'auto-cart-overlay';
    overlay.setAttribute('data-auto-cart-overlay', 'true');

    // Criar conteúdo do overlay
    const content = document.createElement('div');
    content.className = 'auto-cart-overlay-content';
    content.innerHTML = `<span class="auto-cart-overlay-spinner"></span>${message}`;

    // Adicionar conteúdo ao overlay
    overlay.appendChild(content);

    // Adicionar overlay ao botão
    checkoutButton.appendChild(overlay);

    // Desabilitar o botão
    checkoutButton.disabled = true;

    // Salvar referência ao overlay
    currentOverlay = overlay;

    log(`✓ Overlay criado e sobreposto ao botão`);
  }

  function unlockCheckoutButtons() {
    log('🔓 Removendo overlay do botão...');
    isProcessingGifts = false;

    const checkoutButton = document.querySelector(CONFIG.CHECKOUT_BUTTON_SELECTOR);
    
    if (!checkoutButton) {
      log('⚠️ Botão de checkout não encontrado para desbloquear');
      return;
    }

    // Se não existe overlay, não fazer nada
    if (!currentOverlay) {
      log('ℹ Nenhum overlay para remover');
      return;
    }

    // Remover overlay do DOM
    if (currentOverlay.parentNode === checkoutButton) {
      checkoutButton.removeChild(currentOverlay);
    }

    // Reabilitar o botão
    checkoutButton.disabled = false;

    // Limpar referência
    currentOverlay = null;

    log(`✓ Overlay removido e botão reabilitado`);
  }

  // ========== SISTEMA DE NOTIFICAÇÕES ==========
  function createNotificationContainer() {
    let container = document.getElementById('auto-cart-notifications');
    if (!container) {
      container = document.createElement('div');
      container.id = 'auto-cart-notifications';
      container.style.cssText = `position: fixed; top: 20px; right: 20px; z-index: 10000; pointer-events: none; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;`;
      document.body.appendChild(container);
    }
    return container;
  }

  function showNotification(message) {
    if (!CONFIG.NOTIFICATION.ENABLED) return;
    const container = createNotificationContainer();
    const notification = document.createElement('div');
    notification.style.cssText = `background-color: #4CAF50; color: white; padding: 12px 16px; margin-bottom: 10px; border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); font-size: 14px; font-weight: 500; opacity: 0; transform: translateX(100%); transition: all 0.3s ease; pointer-events: auto; max-width: 300px; word-wrap: break-word;`;
    notification.textContent = message;
    container.appendChild(notification);
    setTimeout(() => {
      notification.style.opacity = '1';
      notification.style.transform = 'translateX(0)';
    }, 10);
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => notification.parentNode?.removeChild(notification), 300);
    }, CONFIG.NOTIFICATION.DURATION);
  }

  // ========== LÓGICA PRINCIPAL DO CARRINHO ==========

  async function clearCart() {
    log('Carrinho com total zero detectado. Limpando todos os itens...');
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        log('Carrinho limpo com sucesso.');
        lastCartState = await response.json();
        giftsInCart.clear();
        startCooldown();
        window.location.reload();
        return true;
      }
      return false;
    } catch (error) {
      log('Erro ao limpar o carrinho:', error);
      return false;
    }
  }

  // ========== FUNÇÃO: Adicionar ou Atualizar Brinde ==========
  async function ensureGiftQuantity(gift, targetQuantity = 1) {
    if (!gift.PRODUCT_VARIANT_ID || giftsBeingManaged.has(gift.PRODUCT_VARIANT_ID)) {
      return false;
    }
    
    const currentQuantity = giftsInCart.get(gift.PRODUCT_VARIANT_ID) || 0;
    
    // Se já tem a quantidade correta, não faz nada
    if (currentQuantity === targetQuantity) {
      log(`Brinde ${gift.NAME} já tem a quantidade correta (${targetQuantity}).`);
      return true;
    }
    
    log(`Ajustando quantidade do brinde ${gift.NAME} de ${currentQuantity} para ${targetQuantity}`);
    giftsBeingManaged.add(gift.PRODUCT_VARIANT_ID);

    try {
      // Usa /cart/update.js para definir quantidade exata
      const formData = { updates: { [gift.PRODUCT_VARIANT_ID]: targetQuantity } };
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const updatedCart = await response.json();
        lastCartState = updatedCart;
        
        if (targetQuantity > 0) {
          log(`Brinde ${gift.NAME} ajustado para ${targetQuantity} unidade(s) com sucesso.`);
          giftsInCart.set(gift.PRODUCT_VARIANT_ID, targetQuantity);
          if (currentQuantity === 0) {
            showNotification(`${gift.NAME} adicionado ao carrinho!`);
          }
        } else {
          log(`Brinde ${gift.NAME} removido com sucesso.`);
          giftsInCart.delete(gift.PRODUCT_VARIANT_ID);
        }
        
        startCooldown();
        hideGiftQuantitySelectors();
        return true;
      } else {
        log(`Falha ao ajustar brinde ${gift.NAME}. Status: ${response.status}`);
        return false;
      }
    } catch (error) {
      log(`Erro de rede ao ajustar brinde:`, error);
      return false;
    } finally {
      giftsBeingManaged.delete(gift.PRODUCT_VARIANT_ID);
    }
  }

  // ========== FUNÇÃO LEGADA MANTIDA PARA COMPATIBILIDADE ==========
  async function removeGiftFromCart(gift) {
    return await ensureGiftQuantity(gift, 0);
  }

  function hideGiftQuantitySelectors() {
    CONFIG.GIFTS.forEach(gift => {
      if (!gift.PRODUCT_VARIANT_ID) return;
      
      // Tenta múltiplos seletores para compatibilidade com diferentes versões
      const selectors = [
        `[href*="/variants/${gift.PRODUCT_VARIANT_ID}"]`,
        `[data-variant-id="${gift.PRODUCT_VARIANT_ID}"]`,
        `[data-product-variant-id="${gift.PRODUCT_VARIANT_ID}"]`
      ];
      
      selectors.forEach(selector => {
        const giftItemElements = document.querySelectorAll(selector);
        giftItemElements.forEach(element => {
          // Tenta múltiplos seletores de container
          const cartItemRow = element.closest(
            '.cart-item, .cart__row, [data-cart-item], .cart-item-row, [data-cart-line-item]'
          );
          
          if (cartItemRow) {
            // Tenta múltiplos seletores de quantity
            const quantitySelector = cartItemRow.querySelector(
              '.quantity, .cart-item__quantity, .product-quantity, .quantity-selector, [data-quantity-input], input[type="number"]'
            );
            
            if (quantitySelector && quantitySelector.style.display !== 'none') {
              log(`Ocultando seletor de quantidade para ${gift.NAME}`);
              quantitySelector.style.display = 'none';
            }
          }
        });
      });
    });
  }

  // ==================================================================
  // ========== FUNÇÃO PRINCIPAL MODIFICADA (checkCartAndUpdate) ==========
  // ==================================================================
  async function checkCartAndUpdate() {
    if (isCheckingCart || isInCooldown()) {
      return;
    }
    isCheckingCart = true;
    log('Verificando carrinho...');

    // BLOQUEIO CORRIGIDO: Bloquear APENAS se houver brindes a processar
    let needsGiftProcessing = false;

    try {
      // Usa fetchCart se disponível (Prestige v7+), senão usa fetch direto
      const cart = await fetchCartFunction();

      if (cart.total_price === 0 && cart.item_count > 0) {
        await clearCart();
        return;
      }

      if (!hasCartStateChanged(cart)) {
        log('Estado do carrinho não mudou. Ignorando.');
        hideGiftQuantitySelectors();
        isCheckingCart = false;
        return;
      }

      log('Estado do carrinho mudou. Processando...');
      lastCartState = cart;

      const giftVariantIds = new Set(CONFIG.GIFTS.map(g => g.PRODUCT_VARIANT_ID));
      cart.items.forEach(item => {
        if (giftVariantIds.has(item.variant_id)) {
          giftsInCart.set(item.variant_id, item.quantity);
          if (item.quantity > 1) {
            log(`⚠️ ALERTA: Brinde com ${item.quantity} unidades detectado! Corrigindo...`);
          }
        }
      });

      let subtotalWithoutGifts = 0;
      let hasOtherItems = false;
      cart.items.forEach(item => {
        if (!giftVariantIds.has(item.variant_id)) {
          subtotalWithoutGifts += item.final_line_price;
          hasOtherItems = true;
        }
      });
      subtotalWithoutGifts /= 100;

      if (giftsInCart.size > 0 && !hasOtherItems) {
        log('Apenas brindes no carrinho. Removendo todos...');
        for (const gift of CONFIG.GIFTS) {
          if (giftsInCart.has(gift.PRODUCT_VARIANT_ID)) {
            await removeGiftFromCart(gift);
          }
        }
        isCheckingCart = false;
        return;
      }

      log(`Subtotal (sem brindes): ${subtotalWithoutGifts.toFixed(2)}`);

      // Verificar se há brindes que precisam ser processados
      for (const gift of CONFIG.GIFTS) {
        const currentQuantity = giftsInCart.get(gift.PRODUCT_VARIANT_ID) || 0;
        const shouldHaveGift = subtotalWithoutGifts >= gift.THRESHOLD;
        
        if ((shouldHaveGift && currentQuantity !== 1) || (!shouldHaveGift && currentQuantity > 0)) {
          needsGiftProcessing = true;
          break;
        }
      }

      // BLOQUEIO CORRIGIDO: Bloquear APENAS se houver processamento necessário
      if (needsGiftProcessing) {
        lockCheckoutButtons();
      }

      // MODIFICAÇÃO PARA SUPORTE CUMULATIVO:
      // Itera sobre todos os brindes configurados e adiciona TODOS os que são elegíveis
      for (const gift of CONFIG.GIFTS) {
        const currentQuantity = giftsInCart.get(gift.PRODUCT_VARIANT_ID) || 0;
        
        // Verifica se o subtotal atinge o valor mínimo para ESTE brinde
        if (subtotalWithoutGifts >= gift.THRESHOLD) {
          // Se o cliente é elegível, garante que o brinde esteja no carrinho com quantidade 1
          if (currentQuantity !== 1) {
            log(`Cliente elegível para ${gift.NAME}. Adicionando/corrigindo quantidade.`);
            await ensureGiftQuantity(gift, 1);
          }
        } else {
          // Se o cliente NÃO é mais elegível, remove o brinde do carrinho
          if (currentQuantity > 0) {
            log(`Cliente não mais elegível para ${gift.NAME}. Removendo.`);
            await ensureGiftQuantity(gift, 0);
          }
        }
      }

      hideGiftQuantitySelectors();

    } catch (error) {
      log('Erro ao verificar o carrinho:', error);
    } finally {
      isCheckingCart = false;
      // BLOQUEIO CORRIGIDO: Desbloquear APENAS se foi bloqueado
      if (needsGiftProcessing) {
        unlockCheckoutButtons();
      }
    }
  }

  // ========== NOVA SEÇÃO: INTERCEPTAÇÃO DE COMPRA DIRETA ==========
  async function handleDirectBuyClick(event) {
    // Verificar se o clique é em um botão de compra
    const target = event.target.closest('button, a, [role="button"]');
    if (!target) return;

    // Padrões de identificação de botões de compra direta
    const buyPatterns = [
      'buy-now',
      'direct-buy',
      'quick-buy',
      'comprar-agora',
      'comprar-direto',
      'buy',
      'checkout',
      'add-to-cart-and-checkout'
    ];

    const elementText = (target.textContent || '').toLowerCase();
    const elementClass = (target.className || '').toLowerCase();
    const elementId = (target.id || '').toLowerCase();
    const dataAction = (target.getAttribute('data-action') || '').toLowerCase();

    // Verificar se é um botão de compra direta
    const isBuyButton = buyPatterns.some(pattern => 
      elementClass.includes(pattern) || 
      elementId.includes(pattern) || 
      elementText.includes(pattern) ||
      dataAction.includes(pattern) ||
      elementText.includes('comprar') ||
      elementText.includes('buy')
    );

    if (!isBuyButton) return;

    // Se já está processando, não fazer nada
    if (isProcessingDirectBuy) {
      log('Já está processando compra direta, ignorando novo clique.');
      return;
    }

    log('🛒 Clique em botão de compra direta detectado! Forçando atualização de brindes...');
    isProcessingDirectBuy = true;

    try {
      // Forçar atualização do carrinho antes de prosseguir
      cooldownUntil = 0; // Remover cooldown
      isCheckingCart = false; // Resetar flag
      
      // Aguardar a verificação e atualização do carrinho
      await checkCartAndUpdate();
      
      // Aguardar um pouco para garantir que os brindes foram adicionados
      await new Promise(resolve => setTimeout(resolve, 500));
      
      log('✓ Brindes atualizados. Prosseguindo com checkout...');
      
    } catch (error) {
      log('Erro ao processar compra direta:', error);
    } finally {
      isProcessingDirectBuy = false;
    }
  }

  // ========== OBSERVADORES E INICIALIZAÇÃO ==========
  function setupEventListeners() {
    // Para Prestige v7+, usar eventos do tema se disponível
    if (isPrestigeV7Plus) {
      // Escuta eventos de atualização do carrinho do tema
      document.addEventListener('cart:updated', () => {
        log('Evento cart:updated detectado');
        const debouncedCheck = () => {
          clearTimeout(debounceTimeout);
          debounceTimeout = setTimeout(checkCartAndUpdate, CONFIG.OPTIMIZATION.DEBOUNCE_DELAY);
        };
        debouncedCheck();
      });
    }
    
    // Fallback: usar MutationObserver para versões anteriores
    const observer = new MutationObserver((mutations) => {
      const relevantMutation = mutations.some(m => 
        m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0)
      );
      if (relevantMutation) {
        log('Mutação detectada no DOM, acionando verificação.');
        const debouncedCheck = () => {
          clearTimeout(debounceTimeout);
          debounceTimeout = setTimeout(checkCartAndUpdate, CONFIG.OPTIMIZATION.DEBOUNCE_DELAY);
        };
        debouncedCheck();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // NOVA SEÇÃO: Interceptar cliques em botões de compra direta
    document.addEventListener('click', handleDirectBuyClick, true);
    log('✓ Listener para compra direta ativado');
  }

  async function init() {
    if (CONFIG.GIFTS.length === 0) return;
    
    // Injetar estilos CSS para overlay
    injectOverlayStyles();
    
    // Detectar versão do Prestige
    await detectPrestigeVersion();
    
    // Executar verificação inicial
    setTimeout(checkCartAndUpdate, 500);
    
    // Configurar listeners
    setupEventListeners();

    // Listener para quando a página fica visível novamente
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        log('Página visível novamente, forçando verificação.');
        cooldownUntil = 0;
        checkCartAndUpdate();
      }
    });
  }

  // ========== AUTO-START ==========
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ========== EXPOSIÇÃO DE FUNÇÕES PARA DEBUG ==========
  window.autoCartDebug = {
    checkNow: checkCartAndUpdate,
    clearCart: clearCart,
    config: CONFIG,
    status: () => ({
      isCheckingCart,
      isInCooldown: isInCooldown(),
      cooldownUntil,
      lastCartState,
      giftsInCart: Array.from(giftsInCart.entries()),
      isPrestigeV7Plus,
      isProcessingDirectBuy,
      isProcessingGifts,
      hasOverlay: currentOverlay !== null
    }),
    resetCooldown: () => { cooldownUntil = 0; },
    forceDirectBuyCheck: () => checkCartAndUpdate(),
    lockButtons: () => lockCheckoutButtons(),
    unlockButtons: () => unlockCheckoutButtons()
  };

})();
